<template>
  <div
    :id="`${source}_hubspot_pipeline_stage`"
    class="w-full flex justify-start ml-2"
  >
    <div class="flex flex-row gap-2">
      <div
        v-for="(stage, index) in visibleStages"
        :key="index"
        class="tags_container"
      >
        {{ stage }}
      </div>

      <div v-if="hiddenStagesCount > 0" class="relative">
        <div
          class="tags_container inline-block cursor-pointer"
          @mouseenter="handleMouseEnter"
          @mouseleave="hideDropdownWithDelay"
          ref="dropdownTriggerRef"
        >
          +{{ hiddenStagesCount }}
        </div>

        <Teleport to="body">
          <div
            v-if="showDropdown"
            class="dropdown_menu"
            :style="dropdownPositionStyle"
            @mouseenter="showDropdown = true"
            @mouseleave="hideDropdownWithDelay"
          >
            {{ hiddenStages.join(", ") }}
          </div>
        </Teleport>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from "vue";

const props = defineProps({
  source: { type: String, default: "custom_table" },
  row: { type: Object, required: true },
});

const showDropdown = ref(false);
const maxVisibleStages = 3;
const dropdownTriggerRef = ref(null);
const dropdownPositionStyle = ref({});
const hideDropdownTimeout = ref(null);

const handleMouseEnter = () => {
  showDropdown.value = true;

  nextTick(() => {
    if (
      dropdownTriggerRef.value &&
      dropdownTriggerRef.value instanceof HTMLElement
    ) {
      const rect = dropdownTriggerRef.value.getBoundingClientRect();
      dropdownPositionStyle.value = {
        position: "fixed",
        top: `${rect.bottom + 5}px`,
        left: `${rect.left}px`,
        zIndex: 9999,
      };
    }
  });
};

const hideDropdownWithDelay = () => {
  hideDropdownTimeout.value = setTimeout(() => {
    showDropdown.value = false;
  }, 150);
};

const clearHideTimeout = () => {
  if (hideDropdownTimeout.value) {
    clearTimeout(hideDropdownTimeout.value);
    hideDropdownTimeout.value = null;
  }
};

const updatePosition = () => {
  if (
    showDropdown.value &&
    dropdownTriggerRef.value &&
    dropdownTriggerRef.value instanceof HTMLElement
  ) {
    const rect = dropdownTriggerRef.value.getBoundingClientRect();
    dropdownPositionStyle.value = {
      position: "fixed",
      top: `${rect.bottom + 5}px`,
      left: `${rect.left}px`,
      zIndex: 9999,
    };
  }
};

onMounted(() => {
  window.addEventListener("scroll", updatePosition, true);
  window.addEventListener("resize", updatePosition);
});

onUnmounted(() => {
  window.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener("resize", updatePosition);

  if (hideDropdownTimeout.value) {
    clearTimeout(hideDropdownTimeout.value);
  }
});

const visibleStages = computed(() => {
  const stages = props.row.stages || [];
  return stages.slice(0, maxVisibleStages);
});

const hiddenStages = computed(() => {
  const stages = props.row.stages || [];
  return stages.slice(maxVisibleStages);
});

const hiddenStagesCount = computed(() => {
  return hiddenStages.value.length;
});
</script>

<style scoped>
.tags_container {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: 20px;
  border: 1px solid var(--white-opacity-8);
  background: var(--white-opacity-6);
  padding: 0.25rem 0.75rem;
  white-space: nowrap;
}

.dropdown_menu {
  background: var(--white-opacity-6) !important;
  backdrop-filter: blur(100px);
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
  color: var(--text-color);
  font-size: 0.875rem;
  line-height: 20px;
  white-space: normal;
  width: max-content;
  max-width: 250px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>
